/**
 * Megamenyns undermenyer — två olika modeller, som i den gamla sajten.
 *
 * DESKTOP (≥768px): hovra en toppnivåpunkt så glider undermenyn in som en EGEN
 * kolumn till höger om panelen. Den stängs 500 ms efter att pekaren lämnat, så
 * att man hinner flytta sig diagonalt ned i kolumnen utan att den försvinner.
 * Nivå 3 lägger sig som en tredje kolumn, men bara från 1200px.
 *
 * MOBIL (<768px): klick fäller ut undermenyn nedåt i samma kolumn.
 *
 * Beteendet är läst ur det klassiska temats routes/common.js och
 * layout/_navigation.scss, inte gissat. Kärnans overlay har varken det ena eller
 * det andra: den renderar alla undermenyer utfällda och hårdkodar
 * aria-expanded="true". Se UTVECKLARBRIEF.md J7 och J12.
 *
 * Progressiv förbättring: utan JS står undermenyerna kvar utfällda, vilket är
 * kärnans läge och fullt användbart.
 */

const OVERLAY = '.bk-megamenu .wp-block-navigation__responsive-container';
const ITEM = '.wp-block-navigation-submenu';
const TOGGLE = '.wp-block-navigation-submenu__toggle';
const OPEN_CLASS = 'bk-submenu-open';
const CLOSE_DELAY = 500;
const DESKTOP = '(min-width: 768px)';

function submenuOf( item: Element ): HTMLElement | null {
	return item.querySelector( ':scope > .wp-block-navigation__submenu-container' );
}

function setOpen( item: Element, open: boolean ): void {
	item.classList.toggle( OPEN_CLASS, open );
	const toggle = item.querySelector( ':scope > ' + TOGGLE );
	if ( toggle ) {
		toggle.setAttribute( 'aria-expanded', open ? 'true' : 'false' );
	}
	const submenu = submenuOf( item );
	if ( submenu ) {
		submenu.hidden = ! open;
	}
}

function closeAll( root: Element, except?: Element ): void {
	root.querySelectorAll( ITEM ).forEach( ( i ) => {
		if ( i !== except ) {
			setOpen( i, false );
		}
	} );
}

export default function megaMenuSubmenus(): void {
	const overlay = document.querySelector( OVERLAY );
	if ( ! overlay ) {
		return;
	}

	const desktop = window.matchMedia( DESKTOP );
	let timer: number | undefined;

	// Stäng allt en gång, nu när vi vet att JS kör.
	closeAll( overlay );

	const items = Array.from( overlay.querySelectorAll( ITEM ) );

	items.forEach( ( item ) => {
		// ── desktop: hover ──
		item.addEventListener( 'mouseenter', () => {
			if ( ! desktop.matches ) {
				return;
			}
			window.clearTimeout( timer );
			// Bara en kolumn i taget på samma nivå.
			const siblings = item.parentElement
				? Array.from( item.parentElement.children )
				: [];
			siblings.forEach( ( s ) => {
				if ( s !== item && s.matches( ITEM ) ) {
					setOpen( s, false );
				}
			} );
			setOpen( item, true );
		} );

		item.addEventListener( 'mouseleave', () => {
			if ( ! desktop.matches ) {
				return;
			}
			window.clearTimeout( timer );
			timer = window.setTimeout( () => setOpen( item, false ), CLOSE_DELAY );
		} );

		// ── mobil: klick på växlaren ──
		const toggle = item.querySelector( ':scope > ' + TOGGLE );
		toggle?.addEventListener( 'click', ( event ) => {
			if ( desktop.matches ) {
				// På desktop öppnar hovern; knappen ska inte kunna låsa läget.
				return;
			}
			event.preventDefault();
			event.stopPropagation();
			setOpen( item, ! item.classList.contains( OPEN_CLASS ) );
		} );
	} );

	// Kärnan återställer aria-expanded när overlayen öppnas; fäll ihop efteråt.
	document
		.querySelector( '.wp-block-navigation__responsive-container-open' )
		?.addEventListener( 'click', () => {
			window.requestAnimationFrame( () => closeAll( overlay ) );
		} );

	// Byter man mellan mobil och desktop ska inget läge hänga kvar.
	desktop.addEventListener( 'change', () => closeAll( overlay ) );
}
